import { httpRequest } from '@/shared/lib/http'

/**
 * Vista de Dirección y su entrevista (SRH-283).
 *
 * Dirección ve solo lo que RH le mandó —`pendiente_direccion`— y decide:
 * aprueba, rechaza, o agenda su propia entrevista cuyo resultado también
 * decide. El endpoint del listado se llamaba `hr-approved` y cambió de nombre
 * con RH-03.1, porque antes listaba todo lo aprobado por RH y ahora solo lo
 * que de verdad llegó a Dirección.
 */

export interface CandidatoParaDireccion {
  candidateId: string
  fullName: string
  status: string
  jobRequest: { id: number; position: string; area: string } | null
  profession: string | null
  experienceYears: number | null
  experienceDescription: string | null
  /** Los montos llegan como número, y los vacíos en nulo, nunca en cero. */
  previousMonthlySalary: number | null
  salaryExpectation: number | null
  declaredSalaryExpectation: number | null
  employmentStatus: string | null
  availability: string | null
  residenceCity: string | null
  /**
   * La entrevista de RH, con todos sus campos en nulo cuando el candidato
   * entró por alta manual y nunca la tuvo.
   */
  hrInterview: {
    score: number | null
    comments: string | null
    interviewerName: string | null
    interviewDate: string | null
  } | null
  /** El rango que RH definió en la vacante, no una propuesta al candidato. */
  hrSalaryRange: { min: number | null; max: number | null } | null
}

export type ModalidadDeEntrevista = 'presencial' | 'virtual'
export type ResultadoDeEntrevista = 'approved' | 'rejected'

export interface EntrevistaDeDireccion {
  id: number
  candidateId: string
  interviewDate: string
  interviewTime: string
  modality: ModalidadDeEntrevista
  locationOrLink: string | null
  interviewer: { userId: string; name: string } | null
  scheduledBy: { userId: string; name: string } | null
  /** Nulo mientras la entrevista sigue pendiente de resultado. */
  result: ResultadoDeEntrevista | null
  reason: string | null
  comments: string | null
  resultRecordedBy: { userId: string; name: string } | null
  resultRecordedAt: string | null
  /** Cuándo se canceló sola al decidir Dirección (SRH-312); nulo si no. */
  cancelledAt?: string | null
}

export interface NuevaEntrevistaDeDireccion {
  candidateId: string
  /** `YYYY-MM-DD` */
  interviewDate: string
  /** `HH:mm` */
  interviewTime: string
  modality: ModalidadDeEntrevista
  locationOrLink?: string
  interviewerUserId: string
}

const decision = (id: string, accion: 'approve' | 'reject') =>
  `/candidates/profile/${id}/direction/${accion}`

export const vistaDeDireccion = {
  /** Lo que RH mandó a Dirección. Los dos filtros son opcionales. */
  async listar(filtros: { jobRequestId?: number; areaId?: number } = {}) {
    const params = new URLSearchParams()
    if (filtros.jobRequestId) params.set('jobRequestId', String(filtros.jobRequestId))
    if (filtros.areaId) params.set('areaId', String(filtros.areaId))
    const cola = params.toString() ? `?${params}` : ''

    const lista = await httpRequest<CandidatoParaDireccion[]>(
      `/candidates/pending-direction${cola}`,
    )
    return lista ?? []
  },

  /** Decisión directa, sin entrevista. Regresa el candidato a RH, que confirma el cierre. */
  async aprobar(candidateId: string): Promise<void> {
    await httpRequest<unknown>(decision(candidateId, 'approve'), {
      method: 'PATCH',
    })
  },

  async rechazar(
    candidateId: string,
    datos: { reason: string; comment?: string },
  ): Promise<void> {
    await httpRequest<unknown>(decision(candidateId, 'reject'), {
      method: 'PATCH',
      body: JSON.stringify(datos),
    })
  },

  /** Agendar no mueve al candidato: se pueden agendar varias. */
  async agendar(datos: NuevaEntrevistaDeDireccion): Promise<void> {
    await httpRequest<unknown>('/candidates/direction-interviews', {
      method: 'POST',
      body: JSON.stringify(datos),
    })
  },

  async entrevistasDe(candidateId: string) {
    const lista = await httpRequest<EntrevistaDeDireccion[]>(
      `/candidates/direction-interviews?candidateId=${candidateId}`,
    )
    return lista ?? []
  },

  /**
   * El resultado sí decide: es la decisión de Dirección y el candidato vuelve
   * a RH, que selecciona o descarta (SRH-311). El primero que se registre gana.
   */
  async registrarResultado(
    entrevistaId: number,
    datos: { result: ResultadoDeEntrevista; reason?: string; comments?: string },
  ): Promise<void> {
    await httpRequest<unknown>(
      `/candidates/direction-interviews/${entrevistaId}/result`,
      { method: 'PATCH', body: JSON.stringify(datos) },
    )
  },
}
